import { useCallback, useEffect, useState } from "react";
import { authApi } from "@/features/auth/api/auth-api";
import { tokenStore } from "@/features/auth/stores/token-store";
import { ApiError } from "@/lib/api-client";

export interface AuthUser {
  id: string;
  email: string | null;
  displayName: string | null;
  totpEnabled: boolean;
  role: "admin";
}

export type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: AuthUser };

export function useAuth() {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setState({ status: "authenticated", user: me.user });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setState({ status: "anonymous" });
      } else {
        // ネットワーク等。安全側で anonymous
        setState({ status: "anonymous" });
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 起動時は token が無いので 401 → refresh → 再試行が走る
      try {
        const me = await authApi.me();
        if (!cancelled) setState({ status: "authenticated", user: me.user });
      } catch {
        if (!cancelled) setState({ status: "anonymous" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (loginId: string, password: string, totpCode?: string) => {
      const res = await authApi.login({ loginId, password, totpCode });
      tokenStore.set(res.accessToken);
      setState({ status: "authenticated", user: res.user });
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      tokenStore.set(null);
      setState({ status: "anonymous" });
    }
  }, []);

  return { state, login, logout, refresh };
}
